import FormSkeleton from '../../Tables/CLA/PremiumPay/components/Form/components/FormSkeleton';
import Box from '@mui/material/Box';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useSaveResource } from 'RestAPIs/useSaveResource';
import { ADMIN_SETTINGS_GROUP } from 'RestAPIs/endpoints';
import { AdminSettingsFormWrapper } from 'components/shared/AdminSettings/AdminSettingsFormWrapper';
import FormFields from './FormFields';
import dayjs, { Dayjs } from 'dayjs';
import { useGetAdminSettings } from '../../../../queries';
import { AdminSettingsResponse } from '../../../types';
import { transformAdminSettingsToFormValues } from '../../../utils';
import { createTimeString } from '../Admin/TravelCostSettings/hooks/useConvertedAdminSettings';
import { settingsAtPayload } from '../utils';

export const SETTINGS_GROUP_NAME = 'work-pattern';

const maxDate = dayjs().endOf('year').add(2, 'year').format('YYYY-MM-DD');

const isWithinLast24Hours = (date) => {
  if (!date) return false;
  const now = dayjs();
  const lastModified = dayjs(date);
  return now.diff(lastModified, 'hour') < 24;
};

const schema = (t) =>
  yup.object({
    workPattern_defaultMorningStartTime: yup.mixed<Dayjs>(),
    workPattern_defaultAfternoonStartTime: yup.mixed<Dayjs>(),
    workPattern_defaultBreakDuration: yup.string().default(''),
    workPattern_defaultHolidayDutyHourType: yup.string().default('').nullable(),
    workPattern_lastModified: yup.date().default(null),
    workPattern_defaultRolloutEndDate: yup
      .date()
      .default(null)
      .test(
        'isWithinLast24Hours',
        t('workPatternRollout.tooManyChangesInAShortPeriod'),
        function () {
          return !isWithinLast24Hours(this.parent.workPattern_lastModified);
        }
      )
      .max(maxDate, t('workPatternRollout.tooFarInFuture'))
  });

const WorkPattern = () => {
  const { t } = useTranslation('common', { keyPrefix: 'workPattern' });

  const { data: workPattern = [], isFetching } =
    useGetAdminSettings<AdminSettingsResponse>(SETTINGS_GROUP_NAME);

  const { isLoading, mutate: save } = useSaveResource({
    url: ADMIN_SETTINGS_GROUP,
    id: workPattern[0]?.groupId,
    invalidateQueryKey: [SETTINGS_GROUP_NAME],
    message: { success: t('saveSuccess'), error: t('saveError') }
  });

  const settings = workPattern[0] ?? undefined;

  const onSubmit = (formData) => {
    save(settingsAtPayload(formData, settings));
  };

  const convertedData = transformAdminSettingsToFormValues(settings);

  return (
    <>
      {settings ? (
        <AdminSettingsFormWrapper
          submitForm={onSubmit}
          schema={schema(t)}
          isLoading={isLoading}
          data={{
            ...convertedData,
            workPattern_defaultMorningStartTime: createTimeString(
              workPattern,
              'workPattern.defaultMorningStartTime'
            ),
            workPattern_defaultAfternoonStartTime: createTimeString(
              workPattern,
              'workPattern.defaultAfternoonStartTime'
            )
          }}
          isFetching={isFetching}
        >
          <FormFields maxDate={maxDate} />
        </AdminSettingsFormWrapper>
      ) : (
        <Box my={3}>
          <FormSkeleton />
        </Box>
      )}
    </>
  );
};

export default WorkPattern;
